import { MutableRefObject, useCallback, useEffect, useRef } from 'react'

import { SectionRendererParams } from '@src/SectionRenderer/SectionRenderer'

import CUSTOM_SECTION_CONTAINER_NAMES from '../../../../constants/customSectionContainerNames'
import CUSTOM_SECTION_CONTAINER_TYPES from '../../../../constants/customSectionContainerTypes'
import { TrackingMetaData } from '../../../../domains/CustomSection'
import GenericContent from '../../../../domains/GenericContent'
import TrackingEvent, { TrackingBrowserEventTypes } from '../../tracking-event'

import { setCustomSectionFindingMethods } from './set-section-finding-methods-utils'
import { getClickEvent, getVisibleEvent } from './use-tracking-event-emitter.helper'

const useTrackingEventEmitter = <E extends HTMLElement>(
  tracking_id?: string,
  tracking_types?: TrackingBrowserEventTypes[],
  content_container_type?: CUSTOM_SECTION_CONTAINER_TYPES,
  content_container_name?: CUSTOM_SECTION_CONTAINER_NAMES,
  id?: string,
  genericContents?: GenericContent[],
  element_position?: number,
  display_title?: string,
  tracking_meta_data?: TrackingMetaData,
  params?: SectionRendererParams,
) => {
  const tracking_types_set = new Set(tracking_types)
  const emitClickEvent = tracking_types_set.has(TrackingEvent.TRACKING_BROWSER_EVENT_TYPES.CLICK)
  const emitVisibleEvent = tracking_types_set.has(TrackingEvent.TRACKING_BROWSER_EVENT_TYPES.VISIBLE)

  const elementRef: MutableRefObject<E | undefined> = useRef<E>()

  const handlePropagatedEvent = useCallback(
    (event: TrackingEvent) => {
      if (tracking_id) {
        event.pushTrackingId(tracking_id)
      }
    },
    [tracking_id],
  )

  const handleClick = useCallback(() => {
    if (!tracking_id) {
      return
    }
    setCustomSectionFindingMethods(params?.tracker, tracking_id, content_container_name)
    if (emitClickEvent) {
      const event = getClickEvent(
        tracking_id,
        content_container_type,
        content_container_name,
        element_position,
        display_title,
        tracking_meta_data,
      )
      elementRef.current?.dispatchEvent(event)
    }
  }, [
    content_container_name,
    content_container_type,
    display_title,
    element_position,
    tracking_id,
    tracking_meta_data,
    emitClickEvent,
    params?.tracker,
  ])

  const handleVisible = useCallback(() => {
    if (!tracking_id) {
      return
    }
    const event = getVisibleEvent(
      tracking_id,
      content_container_type,
      content_container_name,
      id,
      genericContents,
      element_position,
      display_title,
      tracking_meta_data,
    )
    elementRef.current?.dispatchEvent(event)
  }, [
    content_container_name,
    content_container_type,
    display_title,
    element_position,
    genericContents,
    id,
    tracking_id,
    tracking_meta_data,
  ])

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    let observer: IntersectionObserver

    if (emitVisibleEvent) {
      observer = new window.IntersectionObserver((entries: IntersectionObserverEntry[]) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            handleVisible()
            observer?.disconnect()
          }
        })
      })
      observer.observe(element)
    }

    element.addEventListener('click', handleClick)

    return () => {
      element.removeEventListener('click', handleClick)
      element.removeEventListener(TrackingEvent.TRACKING_EVENT_TYPE, handlePropagatedEvent as EventListener)
      observer?.disconnect()
    }
  }, [handlePropagatedEvent, emitVisibleEvent, handleClick, handleVisible, content_container_type])

  if (!tracking_id) return {}

  return {
    elementRef,
  }
}

export default useTrackingEventEmitter
